/**
 * `settings.calendar` — the timezone and the week start, which every view needs before it can
 * lay out a single row (`080-calendar.md` §1.5, `100-settings.md`).
 *
 * The query never *blocks* a view: a calendar that renders nothing until `GET /api/settings`
 * resolves would flash an empty week on every boot, and one that renders an ERROR when
 * settings are unreachable would take the app's default screen down over a preference. So the
 * hook always returns a usable pair, and the stored value simply wins once it arrives.
 */
import { useQuery } from '@tanstack/react-query';
import { keys } from '@/api/keys';
import {
  DEFAULT_WEEK_STARTS_ON,
  detectTimezone,
  getSettings,
  isWeekStartsOn,
  type CalendarSettings,
} from '@/api/endpoints/settings';

export function useCalendarSettings(): CalendarSettings {
  const settings = useQuery({
    queryKey: keys.settings(),
    queryFn: getSettings,
    // `111` §3.2: we are the only writer, and every settings write invalidates this.
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const calendar = settings.data?.calendar;

  return {
    // `100-settings.md`: the stored zone is seeded to `UTC` and the browser's detected zone is
    // what the user actually wants on first run. Until the settings screen (M7) can PATCH it,
    // an untouched `UTC` seed means "not chosen yet" — so we render in the browser's zone
    // rather than showing every evening event an hour or two off.
    timezone:
      calendar?.timezone === undefined || calendar.timezone === 'UTC'
        ? detectTimezone()
        : calendar.timezone,
    weekStartsOn: isWeekStartsOn(calendar?.weekStartsOn)
      ? calendar.weekStartsOn
      : DEFAULT_WEEK_STARTS_ON,
  };
}
